import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Center, Loader, MantineProvider, Paper, Stack, Text } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { AuthProvider } from '@asgardeo/auth-react';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import Cookies from 'js-cookie';
import Dashboard from './pages/Dashboard';
import Help from './pages/Help';
import Tickets from './pages/Tickets';
import DashboardLayout from './layouts/DashboardLayout';
import { OpenAPI } from './types/core/OpenAPI';
import Profile from './pages/Profile/Profile';
import { AdPage } from './pages/AdPage/LandingPage';
import { useEffect, useState } from 'react';
import { ShipmentRoutes } from './routes/ShipmentRoutes';
import { PackageRoutes } from './routes/PackageRoutes';
import { OrderRoutes } from './routes/OrderRoutes';
import { PaymentRoutes } from './routes/PaymentRoutes';
import RulesAndRegulations from "./pages/RulesAndRegulations/RulesAndRegulations";
import { ProfileProvider, useProfile } from './contexts/ProfileContext';
import { PackageFilterProvider } from './contexts/PackageFilterContext';

// Route protection component
const RequireProfile = ({ children }: { children: React.ReactNode }) => {
  const { isProfileComplete, isLoading } = useProfile();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isProfileComplete) {
      navigate('/profile');
    }
  }, [isLoading, isProfileComplete, navigate]);

  if (isLoading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Stack align="center" gap="md">
          <Loader size="xl" color="blue" type="dots" />
          <Text size="sm" c="dimmed">Checking profile...</Text>
          <Text size="xs" c="dimmed">Please complete your profile to continue</Text>
        </Stack>
      </Center>
    );
  }

  if (!isProfileComplete) {
    return null;
  }

  return <>{children}</>;
};
declare global {
  interface Window {
    configs?: {
      back_end_url?: string;
      authConfig?: {
        signInRedirectURL: string;
        signOutRedirectURL: string;
        clientID: string;
        baseUrl: string;
        scope: string[];
      };
    };
  }
}

OpenAPI.BASE = window?.configs?.back_end_url ?? 'http://localhost:8080';
const authConfig: any = window.configs?.authConfig;

function App() {
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const isAuthLoginPath = window.location.pathname === '/auth/login';
  const isAuthCallbackPath = window.location.pathname.includes('/auth/callback');

  // CookieConsent removed

  useEffect(() => {
    const checkAuthentication = async () => {
      // Only show loader when we're on auth-related paths
      if (isAuthLoginPath || isAuthCallbackPath) {
        setIsAuthLoading(true);
      }

      try {
        // Process user information
        const userInfoCookie = Cookies.get('userinfo');
        const storedUserInfo = sessionStorage.getItem("userInfo");
        const authToken = Cookies.get('auth_at');

        if (userInfoCookie) {
          // Save user info to session storage if coming from login
          sessionStorage.setItem("userInfo", userInfoCookie);
          // Extract and store user ID from the JWT token
          try {
            const userInfoData = JSON.parse(userInfoCookie);
            if (userInfoData && userInfoData.sub) {
              localStorage.setItem('userId', userInfoData.sub);


            }
          } catch (parseError) {
          }

          setSignedIn(true);
        } else if (storedUserInfo) {
          // User has already logged in previously
          // Check if userId is in localStorage, if not, try to extract from stored info
          const existingUserId = localStorage.getItem('userId');
          if (!existingUserId) {
            try {
              // Try to parse as base64 first, then as regular JSON
              let userInfoData;
              try {
                userInfoData = JSON.parse(atob(storedUserInfo));
              } catch (base64Error) {
                // If base64 fails, try parsing as regular JSON
                userInfoData = JSON.parse(storedUserInfo);
              }

              if (userInfoData && userInfoData.sub) {
                localStorage.setItem('userId', userInfoData.sub);
              }
            } catch (parseError) {
              // Clear corrupted data
              sessionStorage.removeItem("userInfo");
              setSignedIn(false);
            }
          }
          setSignedIn(true);
        } else {
          setSignedIn(false);
        }

        // Set up the API token for authenticated requests
        if (authToken) {
          OpenAPI.TOKEN = authToken;
          OpenAPI.HEADERS = {
            Authorization: `Bearer ${authToken}`,
          };
        }
      } catch (error) {
        setSignedIn(false);
      } finally {
        setIsAuthLoading(false);
        setIsInitialLoad(false);
      }
    };

    checkAuthentication();
  }, []);





  // Show loader only during authentication process
  if (isAuthLoading) {
    return (
      <MantineProvider defaultColorScheme="light">
        <Center style={{ width: '100vw', height: '100vh' }}>
          <Paper p="xl" withBorder shadow="md" radius="md">
            <Stack align="center" gap="md">
              <Loader size="xl" color="blue" type="dots" />
              <Text fw={600} size="lg" ta="center">Loading your account</Text>
              <Text c="dimmed" size="sm" ta="center">Please wait while we prepare your dashboard</Text>
            </Stack>
          </Paper>
        </Center>
      </MantineProvider>
    );
  }

  // Don't render anything during the initial check to prevent flashes of content
  if (isInitialLoad) {
    return null;
  }

  if (!signedIn) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home/*" element={<AdPage isSign={signedIn} />} />
          {!isAuthLoginPath ? <Route path="*" element={<Navigate to="/home" replace />} /> : null}
        </Routes>
      </Router>
    );
  }

  return (
    <MantineProvider defaultColorScheme="light" >
      <Notifications position="top-right" />
      <Router>
        <ProfileProvider>
          <PackageFilterProvider>
            <Routes>
              <Route
                path="/"
                element={
                  <AuthProvider config={authConfig}>
                    <DashboardLayout />
                  </AuthProvider>
                }
              >
                <Route path="profile" element={<Profile />} />
                <Route
                  path="*"
                  element={
                    <RequireProfile>
                      <Routes>
                        <Route path="dashboard" element={<Dashboard />} />
                        {PackageRoutes()}
                        {ShipmentRoutes()}
                        {OrderRoutes()}
                        {PaymentRoutes()}
                        <Route path="help" element={<Help />} />
                        <Route path="tickets" element={<Tickets />} />
                        <Route path="rules-and-regulations" element={<RulesAndRegulations />} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                      </Routes>
                    </RequireProfile>
                  }
                />
              </Route>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/home/*" element={<AdPage isSign={signedIn} />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
            {/* CookieConsent removed */}
          </PackageFilterProvider>
        </ProfileProvider>
      </Router>
    </MantineProvider>
  );
}
export default App;
